import React, { useState, useEffect } from 'react'
import {
    getAccountSettings,
    AccountSettings,
    updateAccountSettings,
    UpdateAccountSettingsRequest,
    getAllLanguagesAvailable,
    LanguageOption,
    changePassword,
} from '../../api'
import { Toggle } from '../Toggle/Toggle'
import { useDarkMode } from '../../contexts/DarkModeContext'
import PopOutModal from '../PopOutModal/PopOutModal'
import Button from '../ui/Button'
import styles from './AccountSettingsModal.module.css'
import {
    ArrowDown16Filled,
    ArrowUp16Filled,
    ArrowUpRight20Filled,
} from '@fluentui/react-icons'
import { PasswordRequirements } from '../Auth/RegistrationForm'
import DeleteAccountModal from '../DeleteAccountModal/DeleteAccountModal'

const DEFAULT_SETTINGS: AccountSettings = {
    acceptedTerms: false,
    canContact: false,
    darkMode: false,
    showMetrics: true,
    dateAcceptedTerms: null,
    languagePreference: {
        language: 'English (UK)',
        locale: 'en-GB',
    },
    userName: '',
}

interface ContentProps {
    onSaveSuccess: () => void
    setIsLoading: (v: boolean) => void
    onDeleteRequest: () => void
    setIsDirty: (v: boolean) => void
}

const Content: React.FC<ContentProps> = ({
    onSaveSuccess,
    setIsLoading,
    onDeleteRequest,
    setIsDirty,
}) => {
    const { setDarkMode } = useDarkMode()
    const [userSettings, setUserSettings] =
        useState<AccountSettings>(DEFAULT_SETTINGS)
    const [availableLanguages, setAvailableLanguages] = useState<
        LanguageOption[]
    >([])
    const [isFetching, setIsFetching] = useState(true)
    const [showPasswordChange, setShowPasswordChange] = useState(false)
    const [oldPassword, setOldPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordError, setPasswordError] = useState<string | null>(null)
    const [showMoreOptions, setShowMoreOptions] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [settingsData, languages] = await Promise.all([
                    getAccountSettings(undefined),
                    getAllLanguagesAvailable(undefined),
                ])
                setUserSettings(settingsData)
                setAvailableLanguages(languages)
            } catch (err) {
                console.error('Error fetching settings:', err)
            } finally {
                setIsFetching(false)
            }
        }
        fetchData()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setPasswordError(null)

        // Only attempt a password change when the accordion is open and the
        // user has actually typed into the fields.
        const wantsPasswordChange =
            showPasswordChange &&
            (oldPassword !== '' ||
                newPassword !== '' ||
                confirmPassword !== '')

        try {
            if (wantsPasswordChange) {
                if (!oldPassword || !newPassword || !confirmPassword) {
                    setPasswordError('Please fill in all password fields.')
                    return
                }
                if (newPassword !== confirmPassword) {
                    setPasswordError('New passwords do not match.')
                    return
                }
                // Surfaces backend errors (wrong current password, weak new
                // password) via the thrown message.
                await changePassword(oldPassword, newPassword, confirmPassword)
                // Re-mint succeeded server-side; clear the fields and collapse.
                setOldPassword('')
                setNewPassword('')
                setConfirmPassword('')
                setShowPasswordChange(false)
            }

            const updateData: UpdateAccountSettingsRequest = {
                canContact: userSettings.canContact,
                acceptedTerms: userSettings.acceptedTerms,
                languagePreference: userSettings.languagePreference,
            }
            await updateAccountSettings(updateData, undefined)
            setIsDirty(false)
            onSaveSuccess()
        } catch (err) {
            if (wantsPasswordChange) {
                setPasswordError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to change password.'
                )
            } else {
                console.error('Error updating settings:', err)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleLanguageChange = (lang: LanguageOption) => {
        setIsDirty(true)
        setUserSettings((prev) => ({ ...prev, languagePreference: lang }))
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target
        setIsDirty(true)
        setUserSettings((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const handleDarkModeToggle = async (checked: boolean) => {
        setDarkMode(checked)
        setUserSettings((prev) => ({ ...prev, darkMode: checked }))
        try {
            await updateAccountSettings({ darkMode: checked }, undefined)
        } catch (err) {
            console.error('Error updating dark mode:', err)
            setDarkMode(!checked)
            setUserSettings((prev) => ({ ...prev, darkMode: !checked }))
        }
    }

    if (isFetching) {
        return <div>Loading settings...</div>
    }

    return (
        <form
            id="account-settings-form"
            onSubmit={handleSubmit}
            className={styles.form}
        >
            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>General</h2>
                <div className={styles.field}>
                    <label htmlFor="email" className={styles.label}>
                        Email
                    </label>
                    <input
                        id="email"
                        name="email"
                        value={userSettings.userName}
                        onChange={handleChange}
                        className={styles.input}
                        disabled
                    />
                </div>
                <div className={styles.passwordSection}>
                    <div className={styles.field}>
                        <label className={styles.label}>Password</label>
                        <Button
                            variant="secondary"
                            onClick={() => setShowPasswordChange((v) => !v)}
                        >
                            {showPasswordChange ? 'Cancel' : 'Change'}
                        </Button>
                    </div>
                    {showPasswordChange && (
                        <div className={styles.passwordAccordion}>
                            <div className={styles.field}>
                                <label
                                    htmlFor="oldPassword"
                                    className={styles.label}
                                >
                                    Current Password
                                </label>
                                <input
                                    id="oldPassword"
                                    type="password"
                                    value={oldPassword}
                                    onChange={(e) => {
                                        setIsDirty(true)
                                        setPasswordError(null)
                                        setOldPassword(e.target.value)
                                    }}
                                    className={styles.input}
                                    placeholder="Enter current password"
                                    autoComplete="off"
                                />
                            </div>
                            <div className={styles.field}>
                                <label
                                    htmlFor="newPassword"
                                    className={styles.label}
                                >
                                    New Password
                                </label>
                                <input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setIsDirty(true)
                                        setPasswordError(null)
                                        setNewPassword(e.target.value)
                                    }}
                                    className={styles.input}
                                    placeholder="Enter new password"
                                />
                            </div>
                            {newPassword && (
                                <PasswordRequirements password={newPassword} />
                            )}
                            <div className={styles.field}>
                                <label
                                    htmlFor="confirmPassword"
                                    className={styles.label}
                                >
                                    Confirm New Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setIsDirty(true)
                                        setPasswordError(null)
                                        setConfirmPassword(e.target.value)
                                    }}
                                    className={styles.input}
                                    placeholder="Re-enter new password"
                                />
                            </div>
                            {passwordError && (
                                <p className={styles.passwordError} role="alert">
                                    {passwordError}
                                </p>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.passwordSection}>
                    <div className={styles.field}>
                        <label className={styles.label}>
                            More Account Options
                        </label>
                        <button
                            type="button"
                            className={styles.arrowButton}
                            onClick={() => setShowMoreOptions((v) => !v)}
                        >
                            {showMoreOptions ? (
                                <ArrowUp16Filled />
                            ) : (
                                <ArrowDown16Filled />
                            )}
                        </button>
                    </div>
                    {showMoreOptions && (
                        <div className={styles.passwordAccordion}>
                            <Button
                                variant="destructive"
                                onClick={onDeleteRequest}
                            >
                                Delete Account
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <hr className={styles.divider} />

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Preferences</h2>
                {availableLanguages.length > 0 && (
                    <div className={styles.field}>
                        <div className={styles.textContainer}>
                            <label
                                htmlFor="languagePreference"
                                className={styles.label}
                            >
                                Transcription Language
                            </label>
                            <p className={styles.status}>
                                Used for voice to text transcription
                            </p>
                        </div>
                        <select
                            id="languagePreference"
                            value={userSettings.languagePreference.locale}
                            onChange={(e) => {
                                const selected = availableLanguages.find(
                                    (lang) => lang.locale === e.target.value
                                )
                                if (selected) handleLanguageChange(selected)
                            }}
                            className={styles.input}
                        >
                            {availableLanguages.map((lang) => (
                                <option key={lang.locale} value={lang.locale}>
                                    {lang.language}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className={styles.field}>
                    <div className={styles.textContainer}>
                        <h2 className={styles.label}>Dark Mode</h2>
                        <p
                            className={styles.status}
                            style={{
                                color: userSettings.darkMode
                                    ? 'var(--color-text-brand)'
                                    : 'var(--color-text-secondary)',
                            }}
                        >
                            {userSettings.darkMode ? 'On' : 'Off'}
                        </p>
                    </div>
                    <Toggle
                        checked={userSettings.darkMode}
                        onChange={handleDarkModeToggle}
                    />
                </div>
            </div>

            <hr className={styles.divider} />

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Legal and Privacy</h2>
                <div className={styles.field}>
                    <div className={styles.textContainer}>
                        <h2 className={styles.label}>Marketing Preferences</h2>
                        <p className={styles.status}>
                            I agree to be contacted about updates and news
                        </p>
                    </div>
                    <Toggle
                        checked={userSettings.canContact}
                        onChange={(checked) => {
                            setIsDirty(true)
                            setUserSettings((prev) => ({
                                ...prev,
                                canContact: checked,
                            }))
                        }}
                    />
                </div>
                <a
                    href="https://www.lorefully.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.termsAndConditionsContainer}
                >
                    <div className={styles.textContainer}>
                        <h2 className={styles.label}>Terms and Conditions</h2>
                        <p className={styles.status}>
                            By using Lorefully you consent to our terms and
                            conditions
                        </p>
                    </div>
                    <span className={styles.termsButton}>
                        <ArrowUpRight20Filled />
                    </span>
                </a>
            </div>
        </form>
    )
}

interface Props {
    settingsModalOpen: boolean
    setSettingsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const AccountSettingsModal: React.FC<Props> = ({
    settingsModalOpen,
    setSettingsModalOpen,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false)

    const handleClose = () => {
        if (isDirty) {
            setShowUnsavedWarning(true)
        } else {
            setSettingsModalOpen(false)
        }
    }

    const handleDiscard = () => {
        setIsDirty(false)
        setShowUnsavedWarning(false)
        setSettingsModalOpen(false)
    }

    const handleDeleteRequest = () => {
        setSettingsModalOpen(false)
        setDeleteModalOpen(true)
    }

    const bottomContent = showUnsavedWarning ? (
        <div className={styles.unsavedWarning}>
            <p className={styles.unsavedWarningText}>
                You have unsaved changes.
            </p>
            <div className={styles.warningActions}>
                <Button variant="secondary" onClick={handleDiscard}>
                    Discard
                </Button>
                <Button fullWidth onClick={() => setShowUnsavedWarning(false)}>
                    Keep Editing
                </Button>
            </div>
        </div>
    ) : (
        <Button
            fullWidth
            type="submit"
            form="account-settings-form"
            disabled={isLoading}
        >
            {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
    )

    return (
        <>
            <PopOutModal
                title="Account Settings"
                content={
                    <Content
                        onSaveSuccess={() => setSettingsModalOpen(false)}
                        setIsLoading={setIsLoading}
                        onDeleteRequest={handleDeleteRequest}
                        setIsDirty={setIsDirty}
                    />
                }
                isOpen={settingsModalOpen}
                onClose={handleClose}
                bottomContent={bottomContent}
            />
            <DeleteAccountModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            />
        </>
    )
}

export default AccountSettingsModal
